/**
 * Plasma shader utility.
 *
 * Generates 2-D colour plasma frames into a preallocated Uint8Array buffer.
 * Designed for 320×200 retro effect but resolution-agnostic. Pure algorithm –
 * no WebGL side-effects.
 *
 * @module engine/renderer/plasma
 */

import { plasmaGlyphCache, plasmaDensity } from './bitmapFont';

export const BLOB_CELL_SIZE = 8;
export const DEFAULT_BLOB_SCALE = 0.5; // desktop baseline
export const BLOB_DENSITY = plasmaDensity;
export const BLOB_DITHER = 0.12;
export const USE_ORIGINAL_PLASMA = false;
export const USE_LEAF_PLASMA = true;

// Track animation start time to ensure consistent animation
let animationStartTime = Date.now();

export function computeOriginalPlasma(x, y, t) {
  let color = 0;
  color += 0.7 * Math.sin(0.5 * x + t / 5);
  color += 3.0 * Math.sin(1.6 * y + t / 5);
  color += 1.0 * Math.sin(10.0 * (y * Math.sin(t / 2) + x * Math.cos(t / 5)) + t / 2);
  const cx = x + 0.5 * Math.sin(t / 2);
  const cy = y + 0.5 * Math.cos(t / 4);
  color += 0.4 * Math.sin(Math.sqrt(100.0 * cx * cx + 100.0 * cy * cy + 1.0) + t);
  color += 0.9 * Math.sin(Math.sqrt(75.0 * cx * cx + 25.0 * cy * cy + 1.0) + t);
  color += -1.4 * Math.sin(Math.sqrt(256.0 * cx * cx + 25.0 * cy * cy + 1.0) + t);
  color += 0.3 * Math.sin(0.5 * y + x + Math.sin(t));
  return 0.5 + 0.499 * Math.sin(color);
}

// --- Leaf SDF helper --------------------------------------------------------
// Refined seven-petal cannabis leaf composed of rotated ellipses for production quality
function leafSignedDistance(px, py, t) {
  // normalised coords –1..1, pulsating scale with smoother animation
  const s = 0.92 + 0.08 * Math.sin(t * 0.5);
  const x = px * s;
  const y = py * s;
  let d = 1e9;
  const petals = 7;
  for (let i = 0; i < petals; i++) {
    const a = (i - (petals - 1) / 2) * (Math.PI / (petals + 1));
    const ca = Math.cos(a);
    const sa = Math.sin(a);
    // rotate point by –a
    const rx = x * ca + y * sa;
    const ry = -x * sa + y * ca;
    // ellipse radii vary per petal to mimic shape (centre petals shorter)
    const rX = 0.32 + 0.05 * Math.abs(i - 3);
    const rY = 0.95;
    // distance to ellipse (approx): (x/rx)^2 + (y/ry)^2 - 1
    const dist = (rx * rx) / (rX * rX) + (ry * ry) / (rY * rY) - 1;
    d = Math.min(d, dist);
  }
  return d;
}

export function computeLeafPlasma(x, y, t, w, h) {
  // map grid to [-1,1] keeping aspect
  const nx = (x / w) * 2 - 1;
  const ny = (y / h) * 2 - 1;
  const dist = leafSignedDistance(nx, ny, t);
  // convert signed distance to brightness (inside leaf brighter)
  const v = 0.5 + 0.5 * Math.cos(dist * 10 - t * 2);
  return v;
}

export function renderBlobBackground(ctx, time, bufferW, bufferH) {
  // Use a stable time value for animation
  if (!time) {
    time = (Date.now() - animationStartTime) / 1000;
  }

  // Ensure plasma glyph cache is loaded before rendering
  if (!plasmaGlyphCache.loaded) {
    // Try to load the plasma glyphs if not already loaded
    plasmaGlyphCache.load().catch(error => {
      console.log('Failed to load plasma glyphs for blob rendering:', error.message);
    });
    return;
  }

  // Ensure we have valid plasma glyphs
  if (!plasmaGlyphCache.glyphs || Object.keys(plasmaGlyphCache.glyphs).length === 0) {
    console.log('Plasma glyph cache is empty, skipping blob render');
    return;
  }

  // Dynamically adjust resolution based on device for performance
  const isMobile = typeof navigator !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const scale = isMobile ? 0.35 : DEFAULT_BLOB_SCALE;

  const blobW = Math.floor(bufferW * scale);
  const blobH = Math.floor(bufferH * scale);
  
  // PHANTOM CODE ELIMINATED: Create or update blob buffer with performance optimization
  if (!renderBlobBackground.blobBuffer || 
      renderBlobBackground.blobBuffer.width !== blobW || 
      renderBlobBackground.blobBuffer.height !== blobH) {
    renderBlobBackground.blobBuffer = document.createElement('canvas');
    renderBlobBackground.blobBuffer.width = blobW;
    renderBlobBackground.blobBuffer.height = blobH;
    
    // Only log during significant size changes to reduce console spam
    if (typeof window !== 'undefined' && window.location.hostname === 'thedopechainwars.com') {
      const sizeKey = `${blobW}x${blobH}`;
      if (!window.DCW_BLOB_SIZE_LOGGED || window.DCW_LAST_BLOB_SIZE !== sizeKey) {
        console.log(`Blob: Updated buffer to ${blobW}×${blobH} (buffer: ${bufferW}×${bufferH})`);
        window.DCW_BLOB_SIZE_LOGGED = true;
        window.DCW_LAST_BLOB_SIZE = sizeKey;
      }
    }
  }
  // Get context with willReadFrequently for potential performance optimization
  const bctx = renderBlobBackground.blobBuffer.getContext('2d', { willReadFrequently: true });
  bctx.clearRect(0, 0, blobW, blobH);

  // Apply a dark green background for better contrast
  bctx.fillStyle = 'rgba(0, 12, 0, 0.3)';
  bctx.fillRect(0, 0, blobW, blobH);

  const gridCols = Math.floor(blobW / BLOB_CELL_SIZE);
  const gridRows = Math.floor(blobH / BLOB_CELL_SIZE);
  const clamp = (v, min = 0, max = 1) => (v < min ? min : v > max ? max : v);

  // Use a consistent random seed for each frame to reduce flickering
  const frameSeed = Math.floor(time * 10);
  const pseudoRandom = (x, y) => {
    const a = Math.sin(x * 12.9898 + y * 78.233 + frameSeed) * 43758.5453;
    return a - Math.floor(a);
  };

  // Use a pre-computed color palette for better performance and visual quality
  const greenPalette = [];
  for (let i = 0; i < 256; i++) {
    const intensity = i / 255;
    // Create a gradient from dark green to bright green
    greenPalette.push(`rgba(${Math.floor(57 * intensity)}, ${Math.floor(57 + 198 * intensity)}, ${Math.floor(20 * intensity)}, ${0.7 + 0.3 * intensity})`);
  }

  for (let y = 0; y < gridRows; y++) {
    for (let x = 0; x < gridCols; x++) {
      let v;
      if (USE_LEAF_PLASMA) {
        v = computeLeafPlasma(x, y, time, gridCols, gridRows);
      } else if (USE_ORIGINAL_PLASMA) {
        v = computeOriginalPlasma(x, y, time);
      } else {
        v = 0.5 + 0.5 * Math.sin(x * 0.25 + time * 1.2) * Math.cos(y * 0.22 + time * 0.8);
      }
      
      // Use consistent pseudo-random noise instead of Math.random()
      v = clamp(v + (pseudoRandom(x, y) - 0.5) * BLOB_DITHER);
      
      const charIdx = Math.floor(v * (BLOB_DENSITY.length - 1));
      const ch = BLOB_DENSITY[charIdx];
      
      // Try to get the glyph from plasma cache
      const glyph = plasmaGlyphCache.glyphs[ch];
      
      if (glyph) {
        // Apply a slight green tint to the glyphs with varying opacity
        bctx.globalAlpha = 0.85 + v * 0.15; // Vary opacity based on value
        bctx.drawImage(glyph, x * BLOB_CELL_SIZE, y * BLOB_CELL_SIZE, BLOB_CELL_SIZE, BLOB_CELL_SIZE);
      } else {
        // If no glyph available, draw a simple pixel-based representation with color from palette
        const colorIndex = Math.floor(v * 255);
        bctx.fillStyle = greenPalette[colorIndex];
        bctx.fillRect(x * BLOB_CELL_SIZE, y * BLOB_CELL_SIZE, BLOB_CELL_SIZE, BLOB_CELL_SIZE);
      }
    }
  }

  // Reset opacity
  bctx.globalAlpha = 1.0;

  // Apply the blob to the main canvas with proper scaling
  ctx.save();
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(renderBlobBackground.blobBuffer, 0, 0, bufferW, bufferH);
  
  // Add a subtle green glow effect
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = 'rgba(0, 40, 0, 0.07)';
  ctx.fillRect(0, 0, bufferW, bufferH);
  
  // Add a subtle vignette effect for depth
  const gradient = ctx.createRadialGradient(
    bufferW/2, bufferH/2, bufferH * 0.3,
    bufferW/2, bufferH/2, bufferH * 0.9
  );
  gradient.addColorStop(0, 'rgba(0,0,0,0)');
  gradient.addColorStop(1, 'rgba(0,0,0,0.4)');
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, bufferW, bufferH);
  
  ctx.restore();
} 